import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const CART_STORAGE_KEY = 'diy_ppf_cart';

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load from localStorage on init
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [orderId, setOrderId] = useState(null);

  // Save to localStorage whenever cartItems changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
    }
  }, [cartItems]);

  const addToCart = useCallback((item) => {
    const itemWithId = { ...item, cartId: Date.now().toString() };
    setCartItems(prev => [...prev, itemWithId]);
  }, []);

  const removeFromCart = useCallback((cartId) => {
    setCartItems(prev => prev.filter(item => item.cartId !== cartId));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
    setOrderId(null);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch {}
  }, []);

  const setCurrentOrderId = useCallback((id) => {
    setOrderId(id);
  }, []);

  const getCartTotal = useCallback(() => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.pricing.product_price + item.pricing.installation_kit_price, 0);
    const shipping = cartItems.length > 0 ? 35 : 0;
    return { subtotal, shipping, total: subtotal + shipping };
  }, [cartItems]);

  const value = {
    cartItems,
    orderId,
    addToCart,
    removeFromCart,
    clearCart,
    setCurrentOrderId,
    getCartTotal,
    hasItemsInCart: cartItems.length > 0,
    itemCount: cartItems.length
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
